import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import CoherenceMeter from "./Coherence";
import React, { useState, useRef } from "react";
import { FiUpload, FiX, FiCheckCircle } from "react-icons/fi";
import { FaFileExcel } from "react-icons/fa";
// Local configuration mapping - this will be used to map server IDs to configs
const UPLOADER_CONFIGS = {
    extract_file_upload: {
        id: "extract_file_upload",
        testScriptLabel: "Upload Blueprint",
        blueprintLabel: "Upload PDF File",
        submitLabel: "Extract Data",
        testScriptAcceptedTypes: ".xlsx,.xls",
        blueprintAcceptedTypes: ".pdf",
        maxFileSize: 25,
        hideTestScript: true,
        apiCallConfig: {
            endpoint: "/api/cron/extract",
            method: "POST",
            testScriptFieldName: "excel_file",
            blueprintFieldName: "pdf_files",
            jobRequestFieldName: "job_request",
            jobRequestData: {
                job_name: "Extract Job",
                excel_schema: "scora_blueprint",
            },
        },
        description: "Note: Extracts questions based on uploaded documents. Poor quality documents may lead to inaccurate results.",
    },
    generate_file_upload: {
        id: "generate_file_upload",
        testScriptLabel: "Upload Blueprint *",
        blueprintLabel: "Upload supporting documents (Optional)",
        submitLabel: "Generate Data",
        testScriptAcceptedTypes: ".xlsx,.xls",
        blueprintAcceptedTypes: ".pdf",
        maxFileSize: 25,
        allowMultipleBlueprints: true,
        sampleBlueprintUrl: `${process.env.REACT_APP_BACKEND_URL}/api/scora-flow/sample-blueprint`,
        apiCallConfig: {
            endpoint: "/api/cron/generate",
            method: "POST",
            testScriptFieldName: "excel_file",
            blueprintFieldName: "pdf_files",
            jobRequestFieldName: "job_request",
            jobRequestData: {
                job_name: "Extract Job",
                excel_schema: "scora_blueprint",
            },
        },
        description: "Note: Blueprint upload is compulsory. Documents are optional.",
    },
    document_pair_uploader: {
        id: "document_pair_uploader",
        testScriptLabel: "Upload Document A",
        blueprintLabel: "Upload Document B",
        submitLabel: "Process Documents",
        testScriptAcceptedTypes: ".pdf,.docx",
        blueprintAcceptedTypes: ".pdf,.docx",
        maxFileSize: 50,
        apiCallConfig: {
            endpoint: "/api/upload-document-pair",
            method: "POST",
            testScriptFieldName: "document_a",
            blueprintFieldName: "document_b",
            jobRequestFieldName: "config",
            jobRequestData: {
                process_type: "comparison",
                output_format: "json",
            },
        },
    },
    test_blueprint_uploader: {
        id: "test_blueprint_uploader",
        testScriptLabel: "Upload Test Script",
        blueprintLabel: "Upload Blueprint",
        submitLabel: "Submit",
        testScriptAcceptedTypes: ".pdf,.docx,.txt",
        blueprintAcceptedTypes: ".pdf,.docx,.txt,.xlsx",
        maxFileSize: 25,
        // Fallback to old uploadEndpoint for backwards compatibility
        uploadEndpoint: "/api/upload-test-blueprint",
    },
    // Add more configurations as needed
};
const Uploader = ({ config: serverConfig, onSubmit, onJobComplete, onRemoveFromChat, styles = {}, isExpanded = false, sessionId, authToken, getCopilotResponseStreaming, sessionVariables, setJobId: setParentJobId, }) => {
    // Get local config based on server-provided ID, with fallback to server config
    const localConfig = UPLOADER_CONFIGS[serverConfig.id] || serverConfig;
    const [testScriptFile, setTestScriptFile] = useState(null);
    const [progress, setProgress] = useState(0);
    const [blueprintFile, setBlueprintFile] = useState(null);
    const [blueprintFiles, setBlueprintFiles] = useState([]); // For multiple blueprint files
    const [testScriptError, setTestScriptError] = useState("");
    const [blueprintError, setBlueprintError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState("");
    const [jobId, setJobId] = useState("");
    const [isPolling, setIsPolling] = useState(false);
    const [currentPhase, setCurrentPhase] = useState("");
    const [jobStatus, setJobStatus] = useState("");
    const [jobCompleted, setJobCompleted] = useState(false);
    const [similarityScore, setSimilarityScore] = useState("");
    const [hasCompletedWithError, setHasCompletedWithError] = useState(false);
    const [responseSent, setResponseSent] = useState(false);
    const testScriptInputRef = useRef(null);
    const blueprintInputRef = useRef(null);
    const pollingIntervalRef = useRef(null);
    const [visualProgress, setVisualProgress] = useState(0);
    const PHASE_PROGRESS_MAP = {
        sampling: 25,
        extraction: 50,
        generation: 50,
        transformation: 50,
        matching: 75,
        validation: 75,
        completed: 100,
        failed: 100
    };
    const DISPLAY_PHASE_MAP = {
        sampling: "Analyzing Structure",
        extraction: "Extracting Data Points",
        generation: "Generating Content",
        transformation: "Formatting Output",
        matching: "Verifying Consistency",
        validation: "Final Validation",
        completed: "Completed",
        failed: "Failed",
        initializing: "Initializing...",
        queued: "Queued...",
        processing: "Processing..."
    };
    // Windows XP style progressive loader
    React.useEffect(() => {
        if (!isPolling && progress === 0) {
            setVisualProgress(0);
            return;
        }
        const interval = setInterval(() => {
            setVisualProgress((prev) => {
                // If we are significantly behind the target, move faster
                if (prev < progress) {
                    const diff = progress - prev;
                    // User defined speed: "0.05 in 1 second" (0.05% per second)
                    // 50ms interval = 20 ticks/sec
                    // 0.05 / 20 = 0.0025 per tick
                    const slowCrawl = 0.0025;
                    // If we are far behind (new phase), catch up faster
                    // Threshold: if diff > 5, we assume phase jump and speed up
                    // Otherwise we crawl
                    const increment = diff > 5 ? Math.max(slowCrawl, diff * 0.05) : slowCrawl;
                    return Math.min(prev + increment, progress);
                }
                return prev;
            });
        }, 50);
        return () => clearInterval(interval);
    }, [progress, isPolling]);
    const defaultStyles = {
        container: {
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            padding: "20px",
            backgroundColor: "#f8f9fa",
            borderRadius: "12px",
            border: "1px solid #e9ecef",
            margin: "12px 0",
            fontFamily: '"IBM Plex Sans", sans-serif',
            maxWidth: isExpanded ? "100%" : "500px",
        },
        title: {
            fontSize: "16px",
            fontWeight: "600",
            color: "#0E497A",
            marginBottom: "8px",
        },
        uploadsContainer: {
            display: "flex",
            flexDirection: isExpanded ? "row" : "column",
            gap: "16px",
            width: "100%",
        },
        uploadBox: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "8px",
        },
        uploadArea: {
            border: "2px dashed #d1d5db",
            borderRadius: "8px",
            padding: "24px",
            textAlign: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
            backgroundColor: "#ffffff",
            minHeight: "100px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
        },
        uploadAreaActive: {
            borderColor: "#0E497A",
            backgroundColor: "#f1f8fe",
        },
        uploadAreaHover: {
            borderColor: "#6b7280",
            backgroundColor: "#f9fafb",
        },
        uploadIcon: {
            color: "#6b7280",
            marginBottom: "8px",
        },
        uploadLabel: {
            fontSize: "14px",
            fontWeight: "500",
            color: "#374151",
            marginBottom: "4px",
        },
        uploadHint: {
            fontSize: "12px",
            color: "#6b7280",
        },
        filePreview: {
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            backgroundColor: "#e7f3ff",
            borderRadius: "6px",
            border: "1px solid #b3d9ff",
        },
        fileName: {
            fontSize: "14px",
            color: "#0E497A",
            fontWeight: "500",
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        },
        removeButton: {
            background: "none",
            border: "none",
            color: "#6b7280",
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        errorMessage: {
            color: "#dc2626",
            fontSize: "12px",
            marginTop: "4px",
        },
        successMessage: {
            color: "#10b981",
            fontSize: "14px",
            fontWeight: "500",
            textAlign: "center",
            padding: "12px",
            backgroundColor: "#d1fae5",
            borderRadius: "6px",
            border: "1px solid #a7f3d0",
            marginBottom: "8px",
        },
        submitErrorMessage: {
            color: "#dc2626",
            fontSize: "14px",
            fontWeight: "500",
            textAlign: "center",
            padding: "12px",
            backgroundColor: "#fee2e2",
            borderRadius: "6px",
            border: "1px solid #fecaca",
            marginBottom: "8px",
        },
        pollingContainer: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "20px",
            padding: "40px 20px",
            textAlign: "center",
        },
        pollingSpinner: {
            width: "48px",
            height: "48px",
            border: "4px solid #e5e7eb",
            borderTop: "4px solid #0E497A",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
        },
        pollingMessage: {
            fontSize: "16px",
            fontWeight: "500",
            color: "#374151",
            marginBottom: "8px",
        },
        pollingPhase: {
            fontSize: "14px",
            color: "#6b7280",
            fontStyle: "italic",
        },
        jobCompletedSuccess: {
            color: "#10b981",
            fontSize: "16px",
            fontWeight: "600",
        },
        jobCompletedError: {
            color: "#dc2626",
            fontSize: "16px",
            fontWeight: "600",
        },
        submitButton: {
            backgroundColor: "#0E497A",
            color: "white",
            border: "none",
            borderRadius: "8px",
            padding: "12px 24px",
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
            transition: "all 0.2s ease",
            alignSelf: "center",
            minWidth: "120px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            fontFamily: '"IBM Plex Sans", sans-serif',
        },
        submitButtonDisabled: {
            backgroundColor: "#e5e7eb",
            color: "#9ca3af",
            cursor: "not-allowed",
        },
        submitButtonHover: {
            backgroundColor: "#0A3B5C",
        },
        hiddenInput: {
            display: "none",
        },
        downloadLink: {
            fontSize: "13px",
            color: "#0E497A",
            textDecoration: "none",
            fontWeight: "500",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            marginTop: "8px",
            padding: "8px 12px",
            backgroundColor: "#f1f8fe",
            borderRadius: "6px",
            border: "1px solid #b3d9ff",
            transition: "all 0.2s ease",
            cursor: "pointer",
            alignSelf: "center",
            width: "fit-content",
            fontFamily: '"IBM Plex Sans", sans-serif',
        },
        downloadLinkHover: {
            backgroundColor: "#e7f3ff",
            borderColor: "#0E497A",
        },
    };
    const mergedStyles = { ...defaultStyles, ...styles };
    const validateFile = (file, acceptedTypes, maxSize = 25) => {
        // Check file size (convert MB to bytes)
        if (file.size > maxSize * 1024 * 1024) {
            return `File size exceeds ${maxSize}MB limit`;
        }
        // Check file type if specified
        if (acceptedTypes) {
            const extensions = acceptedTypes
                .split(",")
                .map((ext) => ext.trim().toLowerCase());
            const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
            if (!extensions.includes(fileExtension)) {
                return `File type not allowed. Accepted types: ${acceptedTypes}`;
            }
        }
        return "";
    };
    const handleTestScriptChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            const error = validateFile(file, localConfig.testScriptAcceptedTypes, localConfig.maxFileSize);
            if (error) {
                setTestScriptError(error);
                return;
            }
            setTestScriptFile(file);
            setTestScriptError("");
        }
    };
    const handleBlueprintChange = (e) => {
        const files = e.target.files;
        if (files) {
            if (localConfig.allowMultipleBlueprints) {
                // Handle multiple files
                const newFiles = [];
                const errors = [];
                for (let i = 0; i < files.length; i++) {
                    const file = files[i];
                    const error = validateFile(file, localConfig.blueprintAcceptedTypes, localConfig.maxFileSize);
                    if (error) {
                        errors.push(`${file.name}: ${error}`);
                    }
                    else {
                        newFiles.push(file);
                    }
                }
                if (errors.length > 0) {
                    setBlueprintError(errors.join("; "));
                    return;
                }
                // Append to existing files instead of replacing them
                setBlueprintFiles((prevFiles) => [...prevFiles, ...newFiles]);
                setBlueprintFile(null); // Clear single file state
                setBlueprintError("");
            }
            else {
                // Handle single file
                const file = files[0];
                const error = validateFile(file, localConfig.blueprintAcceptedTypes, localConfig.maxFileSize);
                if (error) {
                    setBlueprintError(error);
                    return;
                }
                setBlueprintFile(file);
                setBlueprintFiles([]); // Clear multiple files state
                setBlueprintError("");
            }
        }
    };
    const removeTestScript = () => {
        setTestScriptFile(null);
        setTestScriptError("");
        if (testScriptInputRef.current) {
            testScriptInputRef.current.value = "";
        }
    };
    const removeBlueprint = (indexToRemove) => {
        if (localConfig.allowMultipleBlueprints &&
            typeof indexToRemove === "number") {
            // Remove specific file from multiple files
            setBlueprintFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
        }
        else {
            // Clear all blueprint files
            setBlueprintFile(null);
            setBlueprintFiles([]);
        }
        setBlueprintError("");
        if (blueprintInputRef.current) {
            blueprintInputRef.current.value = "";
        }
    };
    const handleSubmit = async () => {
        const hasFiles = testScriptFile || blueprintFile || blueprintFiles.length > 0;
        if (!hasFiles) {
            return; // Don't submit if no files selected
        }
        // For extract_file_upload, at least PDF file is required
        if (localConfig.id === "extract_file_upload" &&
            !blueprintFile &&
            blueprintFiles.length === 0) {
            setBlueprintError("PDF file is required");
            return;
        }
        setIsSubmitting(true);
        setSubmitError(""); // Clear previous errors
        setSubmitSuccess(""); // Clear previous success messages
        try {
            const files = {
                testScript: testScriptFile || undefined,
                blueprint: localConfig.allowMultipleBlueprints
                    ? blueprintFiles.length > 0
                        ? blueprintFiles
                        : undefined
                    : blueprintFile || undefined,
            };
            // Handle API call directly using configuration
            const result = await handleDirectApiCall(files);
            console.log("Upload successful:", result);
            // Check if we got a job_id in the response
            if (result && result.job_id) {
                setJobId(result.job_id);
                // Update parent context with new jobId
                setParentJobId?.(result.job_id);
                setIsPolling(true);
                setCurrentPhase("initializing");
                setJobStatus(result.status || "queued");
                setHasCompletedWithError(false); // Reset error state for new job
                setResponseSent(false); // Reset response tracking for new job
                // Start polling for job status
                startJobPolling(result.job_id);
                // Call onSubmit callback if provided (for additional parent handling like success messages)
                if (onSubmit) {
                    await onSubmit(files);
                }
            }
            else {
                // Fallback to old behavior if no job_id
                setSubmitSuccess("Files uploaded successfully!");
                // Clear files after successful submission
                setTestScriptFile(null);
                setBlueprintFile(null);
                setBlueprintFiles([]); // Clear multiple blueprint files
                if (testScriptInputRef.current)
                    testScriptInputRef.current.value = "";
                if (blueprintInputRef.current)
                    blueprintInputRef.current.value = "";
                // Call onSubmit callback if provided
                if (onSubmit) {
                    await onSubmit(files);
                }
                // Clear success message after a delay
                setTimeout(() => setSubmitSuccess(""), 3000);
            }
        }
        catch (error) {
            console.error("Error submitting files:", error);
            const errorMessage = error instanceof Error
                ? error.message
                : "Upload failed. Please try again.";
            setSubmitError(errorMessage);
            // Clear error message after a delay
            setTimeout(() => setSubmitError(""), 5000);
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleDirectApiCall = async (files) => {
        const apiConfig = localConfig.apiCallConfig;
        // Use new API config if available, otherwise fall back to old uploadEndpoint
        if (apiConfig) {
            const formData = new FormData();
            // Add session ID if available
            if (sessionId) {
                formData.append("session_id", sessionId);
            }
            // Add files using configured field names
            if (files.testScript && apiConfig.testScriptFieldName) {
                formData.append(apiConfig.testScriptFieldName, files.testScript);
            }
            // Handle blueprint files (single or multiple)
            if (files.blueprint && apiConfig.blueprintFieldName) {
                if (Array.isArray(files.blueprint)) {
                    // Multiple files - append each one with the same field name
                    files.blueprint.forEach((file) => {
                        formData.append(apiConfig.blueprintFieldName, file);
                    });
                }
                else {
                    // Single file
                    formData.append(apiConfig.blueprintFieldName, files.blueprint);
                }
            }
            // Add job request data if configured
            if (apiConfig.jobRequestFieldName && apiConfig.jobRequestData) {
                formData.append(apiConfig.jobRequestFieldName, JSON.stringify(apiConfig.jobRequestData));
            }
            const headers = {
                // Don't set Content-Type, let browser set it with boundary for FormData
                ...apiConfig.headers,
            };
            // Add auth token if available (either from config headers or prop)
            if (authToken) {
                headers.Authorization = `Bearer ${authToken}`;
            }
            const response = await fetch(process.env.REACT_APP_BACKEND_URL + apiConfig.endpoint, {
                method: apiConfig.method || "POST",
                headers,
                body: formData,
            });
            function removeSuffixRegex(str, suffix) {
                const regex = new RegExp(`${suffix}$`, 'i');
                return str.replace(regex, '');
            }
            function removePrefixRegex(str, prefix) {
                const regex = new RegExp(`^${prefix}`, 'i');
                return str.replace(regex, '');
            }
            if (!response.ok) {
                const message = await response.text();
                // alert(message);
                const errorMessage = message.split("Errors:")[1];
                // alert(errorMessage);
                if (errorMessage) {
                    throw new Error(removeSuffixRegex(errorMessage.trim(), "]\"}").slice(1));
                }
                else {
                    throw new Error(`Upload failed: ${response.statusText}`);
                }
            }
            const result = await response.json();
            return result;
        }
        else if (localConfig.uploadEndpoint) {
            // Fallback to old uploadEndpoint behavior
            const formData = new FormData();
            if (sessionId) {
                formData.append("session_id", sessionId);
            }
            if (files.testScript) {
                formData.append("test_script", files.testScript);
            }
            // Handle blueprint files (single or multiple) for fallback endpoint
            if (files.blueprint) {
                if (Array.isArray(files.blueprint)) {
                    // Multiple files - append each one with the same field name
                    files.blueprint.forEach((file) => {
                        formData.append("blueprint", file);
                    });
                }
                else {
                    // Single file
                    formData.append("blueprint", files.blueprint);
                }
            }
            const response = await fetch(localConfig.uploadEndpoint, {
                method: "POST",
                body: formData,
            });
            if (!response.ok) {
                throw new Error(`Upload failed: ${response.statusText}`);
            }
            const result = await response.json();
            return result;
        }
        else {
            throw new Error("No API configuration found for upload");
        }
    };
    const startJobPolling = (jobId) => {
        // Poll every 2 seconds
        pollingIntervalRef.current = setInterval(async () => {
            try {
                await pollJobStatus(jobId);
            }
            catch (error) {
                console.error("Error polling job status:", error);
                setSubmitError("Error checking job status. Please refresh and try again.");
                stopJobPolling();
            }
        }, 2000);
        // Also poll immediately
        pollJobStatus(jobId);
    };
    const pollJobStatus = async (jobId) => {
        // Don't poll if already completed with error to prevent duplicate processing
        if (hasCompletedWithError) {
            return;
        }
        const headers = {};
        // Add auth token if available
        const token = authToken || localStorage.getItem("accessToken");
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cron/jobs/${jobId}`, {
            method: "GET",
            headers,
        });
        if (!response.ok) {
            throw new Error(`Failed to get job status: ${response.statusText}`);
        }
        const jobData = await response.json();
        console.log("Job status:", jobData);
        // Update state with job data
        setCurrentPhase(jobData.current_phase || "processing");
        if (jobData.current_phase) {
            const phaseProgress = PHASE_PROGRESS_MAP[jobData.current_phase] ?? 0;
            setProgress((prev) => Math.max(prev, phaseProgress));
        }
        setJobStatus(jobData.status || "running");
        setJobCompleted(jobData.completed || false);
        // Update similarity score if available and Excel file was uploaded
        // Only check similarity if PDFs were also uploaded (blueprintFile or blueprintFiles)
        const hasPdfFiles = blueprintFile || (blueprintFiles && blueprintFiles.length > 0);
        if (jobData.similarity_score && testScriptFile && hasPdfFiles && !hasCompletedWithError) {
            const scoreValue = parseFloat(jobData.similarity_score);
            setSimilarityScore(jobData.similarity_score);
            const coherencePercent = similarityScore
                ? parseFloat(similarityScore) * 100
                : 0;
            // Check if coherence score is below 0.01% threshold
            if (scoreValue < 0.0001) {
                // Prevent duplicate processing
                if (hasCompletedWithError || responseSent)
                    return;
                setHasCompletedWithError(true);
                setResponseSent(true);
                stopJobPolling();
                setJobCompleted(true);
                // Send failure response to server for error handling
                console.log("Coherence score too low, sending #failed# to server");
                if (getCopilotResponseStreaming) {
                    await getCopilotResponseStreaming({
                        query: JSON.stringify({
                            jobId: jobId,
                            status: "failed",
                            accessToken: sessionVariables?.authorization?.access_token,
                            org_id: sessionVariables?.Org_id,
                            author_id: sessionVariables?.Author_ID,
                        }),
                    });
                }
                // Display error message locally as well
                console.log("Displaying local error message for low coherence");
                setSubmitError(`PDF content does not match Excel blueprint sufficiently (Match Confidence: ${(scoreValue * 100).toFixed(0)}%)`);
                // Clear files and reset state after showing error
                setTimeout(() => {
                    setTestScriptFile(null);
                    setBlueprintFile(null);
                    setBlueprintFiles([]); // Clear multiple blueprint files
                    if (testScriptInputRef.current)
                        testScriptInputRef.current.value = "";
                    if (blueprintInputRef.current)
                        blueprintInputRef.current.value = "";
                    setSimilarityScore("");
                    setIsPolling(false);
                    setCurrentPhase("");
                    setJobStatus("");
                    setJobCompleted(false);
                    setSubmitError("");
                    setSubmitSuccess("");
                    setHasCompletedWithError(false);
                    setResponseSent(false);
                    // Remove uploader from chat after showing error
                    if (onRemoveFromChat) {
                        onRemoveFromChat();
                    }
                }, 5000);
                return; // Exit early to prevent further processing
            }
        }
        // Stop polling if job is completed or failed
        if (jobData.completed ||
            jobData.status === "completed" ||
            jobData.status === "failed") {
            stopJobPolling();
            setJobCompleted(true);
            if (jobData.status === "failed") {
                // Prevent duplicate processing
                if (hasCompletedWithError || responseSent)
                    return;
                setHasCompletedWithError(true);
                setResponseSent(true);
                // Send failure response to server
                console.log("About to send failure response for job failed");
                if (getCopilotResponseStreaming) {
                    await getCopilotResponseStreaming({ query: "#failed#" });
                }
                console.log("Sent failure response for job failed");
                // Clear state - don't call onJobComplete as server will handle chat response
                setTestScriptFile(null);
                setBlueprintFile(null);
                setBlueprintFiles([]); // Clear multiple blueprint files
                if (testScriptInputRef.current)
                    testScriptInputRef.current.value = "";
                if (blueprintInputRef.current)
                    blueprintInputRef.current.value = "";
                setSimilarityScore("");
                setIsPolling(false);
                setCurrentPhase("");
                setJobStatus("");
                setJobCompleted(false);
                setSubmitError("");
                setSubmitSuccess("");
                // Remove uploader from chat after failure
                if (onRemoveFromChat) {
                    onRemoveFromChat();
                }
            }
            else {
                // Prevent duplicate processing
                if (hasCompletedWithError || responseSent)
                    return;
                setResponseSent(true);
                // Send success response to server
                console.log("About to send success response");
                if (getCopilotResponseStreaming) {
                    await getCopilotResponseStreaming({
                        query: JSON.stringify({ jobId: jobId, status: "completed" }),
                    });
                }
                console.log("Sent success response");
                // Automatically download the file
                // await downloadFile(jobId);
                // Clear files after successful completion - don't call onJobComplete as server will handle chat response
                setTestScriptFile(null);
                setBlueprintFile(null);
                if (testScriptInputRef.current)
                    testScriptInputRef.current.value = "";
                if (blueprintInputRef.current)
                    blueprintInputRef.current.value = "";
                // Clear state
                setSimilarityScore("");
                setIsPolling(false);
                setCurrentPhase("");
                setJobStatus("");
                setJobCompleted(false);
                setSubmitError("");
                setSubmitSuccess("");
                // Remove uploader from chat after success
                if (onRemoveFromChat) {
                    onRemoveFromChat();
                }
            }
        }
    };
    const stopJobPolling = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };
    const resetUploader = () => {
        setProgress(0);
        stopJobPolling();
        setIsPolling(false);
        setJobId("");
        setCurrentPhase("");
        setJobStatus("");
        setJobCompleted(false);
        setTestScriptFile(null);
        setBlueprintFile(null);
        setBlueprintFiles([]); // Clear multiple blueprint files
        setSubmitError("");
        setSubmitSuccess("");
        setHasCompletedWithError(false);
        setResponseSent(false);
        if (testScriptInputRef.current)
            testScriptInputRef.current.value = "";
        if (blueprintInputRef.current)
            blueprintInputRef.current.value = "";
    };
    // Cleanup polling on unmount
    React.useEffect(() => {
        return () => {
            stopJobPolling();
        };
    }, []);
    const isSubmitDisabled = (() => {
        if (isSubmitting)
            return true;
        // For extract_file_upload, at least PDF file is required
        if (localConfig.id === "extract_file_upload") {
            return !blueprintFile && blueprintFiles.length === 0;
        }
        // For generate_file_upload, only Excel (Blueprint) is required
        if (localConfig.id === "generate_file_upload") {
            return !testScriptFile;
        }
        // For other configs, require at least one file
        return !testScriptFile && !blueprintFile && blueprintFiles.length === 0;
    })();
    // Show polling UI if job is being processed
    if (isPolling) {
        return (_jsxs("div", { style: mergedStyles.container, children: [_jsxs("div", { style: mergedStyles.pollingContainer, children: [!jobCompleted ? (_jsxs(_Fragment, { children: [_jsxs("div", { style: { width: "100%", maxWidth: 320 }, children: [_jsx("div", { style: {
                                                fontSize: "12px",
                                                marginBottom: "6px",
                                                color: "#374151",
                                                fontWeight: 500,
                                            }, children: DISPLAY_PHASE_MAP[currentPhase] || currentPhase.toUpperCase() }), _jsx("div", { style: {
                                                height: "12px",
                                                background: "#e5e7eb",
                                                borderRadius: "6px",
                                                overflow: "hidden",
                                                border: "1px solid #d1d5db",
                                                boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)"
                                            }, children: _jsx("div", { style: {
                                                    width: `${visualProgress}%`,
                                                    height: "100%",
                                                    background: "linear-gradient(180deg, #0E497A 0%, #1a6ba8 100%)",
                                                    transition: "width 0.1s linear",
                                                    borderRadius: "0 4px 4px 0"
                                                } }) }), _jsx("div", { style: {
                                                fontSize: "12px",
                                                marginTop: "6px",
                                                color: "#6b7280",
                                                display: "flex",
                                                justifyContent: "space-between"
                                            }, children: _jsxs("span", { children: [Math.round(visualProgress), "%"] }) })] }), _jsx("div", { style: mergedStyles.pollingMessage, children: jobStatus === 'queued' ? 'Waiting in queue...' : 'Processing your files...' }), _jsx("div", { style: mergedStyles.pollingPhase, children: DISPLAY_PHASE_MAP[currentPhase] || currentPhase }), similarityScore && testScriptFile && (_jsx("div", { style: { marginTop: 16 }, children: _jsx(CoherenceMeter, { value: parseFloat(similarityScore) * 100 }) }))] })) : (_jsx(_Fragment, { children: jobStatus === "failed" ? (_jsx("div", { style: mergedStyles.jobCompletedError, children: "\u274C Job Failed" })) : (_jsxs(_Fragment, { children: [_jsx("div", { style: mergedStyles.jobCompletedSuccess, children: "\u2705 Job Completed Successfully!" }), _jsx("button", { style: {
                                            ...mergedStyles.submitButton,
                                            marginTop: "16px",
                                            backgroundColor: "#6b7280",
                                        }, onClick: resetUploader, children: "Start New Upload" })] })) })), !jobCompleted && (_jsx("button", { style: {
                                ...mergedStyles.submitButton,
                                backgroundColor: "#6b7280",
                                marginTop: "16px",
                            }, onClick: resetUploader, children: "Cancel & Reset" }))] }), submitError && (_jsx("div", { style: mergedStyles.submitErrorMessage, children: submitError })), submitSuccess && (_jsx("div", { style: mergedStyles.successMessage, children: submitSuccess }))] }));
    }
    const renderLabel = (label) => {
        if (!label)
            return null;
        const parts = label.split("*");
        if (parts.length === 1)
            return label;
        return (_jsxs("span", { children: [parts[0], _jsx("span", { style: { color: "#dc2626", marginLeft: "4px" }, children: "*" }), parts.slice(1).join("*")] }));
    };
    return (_jsxs("div", { style: mergedStyles.container, children: [localConfig.description && (_jsx("div", { style: {
                    marginBottom: "16px",
                    padding: "12px",
                    backgroundColor: "#fffbeb",
                    border: "1px solid #fcd34d",
                    borderRadius: "6px",
                    color: "#92400e",
                    fontSize: "13px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                }, children: localConfig.description })), _jsx("input", { ref: testScriptInputRef, type: "file", accept: localConfig.testScriptAcceptedTypes, onChange: handleTestScriptChange, style: mergedStyles.hiddenInput }), _jsx("input", { ref: blueprintInputRef, type: "file", accept: localConfig.blueprintAcceptedTypes, multiple: localConfig.allowMultipleBlueprints, onChange: handleBlueprintChange, style: mergedStyles.hiddenInput }), _jsxs("div", { style: mergedStyles.uploadsContainer, children: [!localConfig.hideTestScript && (_jsxs("div", { style: mergedStyles.uploadBox, children: [_jsx("div", { style: {
                                    ...mergedStyles.uploadArea,
                                    ...(testScriptFile ? mergedStyles.uploadAreaActive : {}),
                                }, onClick: () => !testScriptFile && testScriptInputRef.current?.click(), onMouseEnter: (e) => {
                                    if (!testScriptFile) {
                                        Object.assign(e.currentTarget.style, mergedStyles.uploadAreaHover);
                                    }
                                }, onMouseLeave: (e) => {
                                    if (!testScriptFile) {
                                        Object.assign(e.currentTarget.style, mergedStyles.uploadArea);
                                    }
                                }, children: testScriptFile ? (_jsxs("div", { style: mergedStyles.filePreview, children: [_jsx(FiCheckCircle, { style: { color: "#10b981" } }), _jsx("span", { style: mergedStyles.fileName, children: testScriptFile.name }), _jsx("button", { style: mergedStyles.removeButton, onClick: (e) => {
                                                e.stopPropagation();
                                                removeTestScript();
                                            }, children: _jsx(FiX, {}) })] })) : (_jsxs(_Fragment, { children: [localConfig.testScriptAcceptedTypes?.includes("xls") ? (_jsx(FaFileExcel, { size: 24, style: mergedStyles.uploadIcon })) : (_jsx(FiUpload, { size: 24, style: mergedStyles.uploadIcon })), _jsx("div", { style: mergedStyles.uploadLabel, children: renderLabel(localConfig.testScriptLabel || "Upload Test Script") }), _jsxs("div", { style: mergedStyles.uploadHint, children: ["Click to browse files", localConfig.testScriptAcceptedTypes && (_jsxs("div", { children: ["Accepted: ", localConfig.testScriptAcceptedTypes] }))] })] })) }), testScriptError && (_jsx("div", { style: mergedStyles.errorMessage, children: testScriptError }))] })), _jsxs("div", { style: mergedStyles.uploadBox, children: [_jsx("div", { style: {
                                    ...mergedStyles.uploadArea,
                                    ...(blueprintFile || blueprintFiles.length > 0
                                        ? mergedStyles.uploadAreaActive
                                        : {}),
                                }, onClick: () => {
                                    if (localConfig.allowMultipleBlueprints) {
                                        if (blueprintFiles.length === 0)
                                            blueprintInputRef.current?.click();
                                    }
                                    else {
                                        if (!blueprintFile)
                                            blueprintInputRef.current?.click();
                                    }
                                }, onMouseEnter: (e) => {
                                    const hasFiles = localConfig.allowMultipleBlueprints
                                        ? blueprintFiles.length > 0
                                        : blueprintFile;
                                    if (!hasFiles) {
                                        Object.assign(e.currentTarget.style, mergedStyles.uploadAreaHover);
                                    }
                                }, onMouseLeave: (e) => {
                                    const hasFiles = localConfig.allowMultipleBlueprints
                                        ? blueprintFiles.length > 0
                                        : blueprintFile;
                                    if (!hasFiles) {
                                        Object.assign(e.currentTarget.style, mergedStyles.uploadArea);
                                    }
                                }, children: localConfig.allowMultipleBlueprints ? (
                                // Multiple files mode
                                blueprintFiles.length > 0 ? (_jsxs("div", { style: {
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "8px",
                                        width: "100%",
                                    }, children: [blueprintFiles.map((file, index) => (_jsxs("div", { style: mergedStyles.filePreview, children: [_jsx(FiCheckCircle, { style: { color: "#10b981" } }), _jsx("span", { style: mergedStyles.fileName, children: file.name }), _jsx("button", { style: mergedStyles.removeButton, onClick: (e) => {
                                                        e.stopPropagation();
                                                        removeBlueprint(index);
                                                    }, children: _jsx(FiX, {}) })] }, index))), _jsx("button", { onClick: (e) => {
                                                e.stopPropagation();
                                                blueprintInputRef.current?.click();
                                            }, style: {
                                                background: "#f8f9fa",
                                                border: "1px dashed #0E497A",
                                                borderRadius: "4px",
                                                padding: "8px 12px",
                                                color: "#0E497A",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                fontWeight: "500",
                                                fontFamily: '"IBM Plex Sans", sans-serif',
                                            }, children: "+ Add More Files" })] })) : (_jsxs(_Fragment, { children: [_jsx(FiUpload, { size: 24, style: mergedStyles.uploadIcon }), _jsx("div", { style: mergedStyles.uploadLabel, children: renderLabel(localConfig.blueprintLabel || "Upload Blueprint") }), _jsxs("div", { style: mergedStyles.uploadHint, children: ["Click to browse files (Multiple files allowed)", localConfig.blueprintAcceptedTypes && (_jsxs("div", { children: ["Accepted: ", localConfig.blueprintAcceptedTypes] }))] })] }))) : // Single file mode
                                    blueprintFile ? (_jsxs("div", { style: mergedStyles.filePreview, children: [_jsx(FiCheckCircle, { style: { color: "#10b981" } }), _jsx("span", { style: mergedStyles.fileName, children: blueprintFile.name }), _jsx("button", { style: mergedStyles.removeButton, onClick: (e) => {
                                                    e.stopPropagation();
                                                    removeBlueprint();
                                                }, children: _jsx(FiX, {}) })] })) : (_jsxs(_Fragment, { children: [_jsx(FiUpload, { size: 24, style: mergedStyles.uploadIcon }), _jsx("div", { style: mergedStyles.uploadLabel, children: renderLabel(localConfig.blueprintLabel || "Upload Blueprint") }), _jsxs("div", { style: mergedStyles.uploadHint, children: ["Click to browse files", localConfig.blueprintAcceptedTypes && (_jsxs("div", { children: ["Accepted: ", localConfig.blueprintAcceptedTypes] }))] })] })) }), blueprintError && (_jsx("div", { style: mergedStyles.errorMessage, children: blueprintError }))] })] }), submitSuccess && (_jsx("div", { style: mergedStyles.successMessage, children: submitSuccess })), submitError && (_jsx("div", { style: mergedStyles.submitErrorMessage, children: submitError })), localConfig.sampleBlueprintUrl && (_jsxs("a", { href: localConfig.sampleBlueprintUrl, download: true, style: mergedStyles.downloadLink, onMouseEnter: (e) => {
                    Object.assign(e.currentTarget.style, mergedStyles.downloadLinkHover);
                }, onMouseLeave: (e) => {
                    Object.assign(e.currentTarget.style, mergedStyles.downloadLink);
                }, children: [_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }), _jsx("polyline", { points: "7 10 12 15 17 10" }), _jsx("line", { x1: "12", y1: "15", x2: "12", y2: "3" })] }), "Download Sample Blueprint"] })), _jsx("button", { style: {
                    ...mergedStyles.submitButton,
                    ...(isSubmitDisabled ? mergedStyles.submitButtonDisabled : {}),
                }, onClick: handleSubmit, disabled: isSubmitDisabled, onMouseEnter: (e) => {
                    if (!isSubmitDisabled) {
                        Object.assign(e.currentTarget.style, mergedStyles.submitButtonHover);
                    }
                }, onMouseLeave: (e) => {
                    if (!isSubmitDisabled) {
                        Object.assign(e.currentTarget.style, mergedStyles.submitButton);
                    }
                }, children: isSubmitting ? (_jsxs(_Fragment, { children: [_jsx("div", { style: {
                                width: "16px",
                                height: "16px",
                                border: "2px solid #ffffff40",
                                borderTop: "2px solid #ffffff",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                            } }), "Submitting..."] })) : (localConfig.submitLabel || "Submit") }), _jsx("style", { children: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        ` })] }));
};
export default Uploader;
